import {
  Component,
  OnInit,
  Inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { UtilsService } from 'src/app/shared/utils.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { DynamicFormsCategoryModel } from '../dynamic-forms-category.model';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RBACService } from 'src/app/shared/rbac.service';

@Component({
  selector: 'app-dynamic-forms-categories-edit',
  templateUrl: './dynamic-forms-categories-edit.component.html',
  styleUrls: ['./dynamic-forms-categories-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DynamicFormsCategoriesEditComponent implements OnInit {
  category: DynamicFormsCategoryModel = {} as DynamicFormsCategoryModel;

  constructor(
    private utils: UtilsService,
    private api: ApiRequestService,
    private rbac: RBACService,
    private dialogRef: MatDialogRef<DynamicFormsCategoriesEditComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: any
  ) {}

  ngOnInit() {
    // Determine if we're editing or creating
    const category_id = Number(this.dialogData['category_id']);
    const isEditMode = !!category_id;

    if (isEditMode) {
      // Editing existing category - check view + edit permissions
      if (
        !this.rbac.userCanAccess(
          [
            RBACService.getPermission(
              RBACService.RBAC_F.F_FORMS,
              RBACService.RBAC_A.A_VIEW
            ),
            RBACService.getPermission(
              RBACService.RBAC_F.F_FORMS,
              RBACService.RBAC_A.A_EDIT
            )
          ],
          true
        )
      ) {
        this.rbac.showGenericAccessDeniedMessage();
        this.dialogRef.close();
        return;
      }
    } else {
      // Creating new category - check create permission only
      if (
        !this.rbac.userCanAccess(
          [
            RBACService.getPermission(
              RBACService.RBAC_F.F_FORMS,
              RBACService.RBAC_A.A_CREATE
            )
          ],
          true
        )
      ) {
        this.rbac.showGenericAccessDeniedMessage();
        this.dialogRef.close();
        return;
      }
    }

    // Get the category id
    if (category_id) {
      this.api
        .makeRequest('get', `v2/dynamic-forms-categories/${category_id}`)
        .then((response: DynamicFormsCategoryModel) => {
          this.category = response;
        })
        .catch((errorResponse: any) => {
          this.utils.handleAPIErrors(errorResponse);
        });
    }
  }

  /**
   * Validate the form input and save the data.
   * @param form The NgForm object that is used for validation.
   * @param autoCloseDialog An optional param to indicate if the dialog should be closed or not.
   * @returns Nothing.
   */
  onSubmit(form: NgForm, autoCloseDialog?: boolean) {
    // Validate the submitted form.
    if (!form.valid) {
      this.utils.showFormValidationError('Please enter all required fields.');
      return;
    }

    // Store the request Promise in this.
    let request: Promise<any>;
    let hasErrors: boolean = false;

    if (this.category.id) {
      // Check permissions before updating
      if (
        !this.rbac.userCanAccess(
          [
            RBACService.getPermission(
              RBACService.RBAC_F.F_FORMS,
              RBACService.RBAC_A.A_VIEW
            ),
            RBACService.getPermission(
              RBACService.RBAC_F.F_FORMS,
              RBACService.RBAC_A.A_EDIT
            )
          ],
          true
        )
      ) {
        this.rbac.showGenericAccessDeniedMessage();
        return;
      }

      // Make an update request if the record id is present.
      request = this.api
        .makeRequest(
          'put',
          `v2/dynamic-forms-categories/${this.category.id}`,
          this.category
        )
        .then((response: DynamicFormsCategoryModel) => {
          this.category = response;
          this.utils.showToast('Category updated successfully.');
        })
        .catch((errorResponse: any) => {
          hasErrors = true;
          this.utils.handleAPIErrors(errorResponse);
        });
    } else {
      // Check permissions before creating
      if (
        !this.rbac.userCanAccess(
          [
            RBACService.getPermission(
              RBACService.RBAC_F.F_FORMS,
              RBACService.RBAC_A.A_CREATE
            )
          ],
          true
        )
      ) {
        this.rbac.showGenericAccessDeniedMessage();
        return;
      }

      // Make a "create" request when the record id is not present.
      request = this.api
        .makeRequest('post', `v2/dynamic-forms-categories`, this.category)
        .then((response: DynamicFormsCategoryModel) => {
          this.utils.showToast('Category created successfully.');

          // Check if user has edit permission
          const hasEditPermission = this.rbac.userCanAccess(
            [
              RBACService.getPermission(
                RBACService.RBAC_F.F_FORMS,
                RBACService.RBAC_A.A_VIEW
              ),
              RBACService.getPermission(
                RBACService.RBAC_F.F_FORMS,
                RBACService.RBAC_A.A_EDIT
              )
            ],
            true
          );

          if (hasEditPermission) {
            // User can edit - keep dialog open and switch to edit mode
            this.category = response;
          } else {
            // User cannot edit - close dialog automatically
            this.onClose();
            return;
          }
        })
        .catch((errorResponse: any) => {
          hasErrors = true;
          this.utils.handleAPIErrors(errorResponse);
        });
    }

    // Check if the dialog should close. It should only close if there are no API errors.
    request.finally(() => {
      if (autoCloseDialog && !hasErrors) {
        this.onClose();
      }
    });
  }

  /**
   * Close the dialog and respond with the category object.
   */
  onClose() {
    this.dialogRef.close(this.category);
  }

  protected readonly RBACService = RBACService;
}
